import { AxiosHeaders, AxiosHeaderValue, AxiosError, AxiosResponse, AxiosInstance } from 'axios';
import { Store } from 'vuex';
import uuid from 'uuid/v4';
import some from 'lodash/some';

import {
  SignInRequest,
  SignInResponse,
  GetCustomerResponse,
  CustomerProfile,
  AuthError,
  CustomerDeliveryAddress,
  CreateUserResponse,
  CreateUserRequest,
  CustomerType,
  UpgradeGuestUserRequest,
  MarketingChannel,
  MarketingContext,
  CustomerAuthResponse,
  ApiError,
  ResetPasswordRequest,
  CustomerSubscriptionStatus,
  FinalizeVerificationResponse,
  InitializeVerificationRequest,
  FinalizeVerificationRequest,
  ValidationError
} from '@/models';
import { AppState, resetStore } from '@/store';
import { MUTATIONS as CUSTOMER_MUTATIONS, GETTERS as CUSTOMER_GETTERS } from '@/store/customer';
import { ApiService } from '../api/api.service';
import { RestaurantService } from '@/services/restaurant/restaurant.service';
import { pascalCaseObject, camelCaseObject } from '@/helpers/formatting';
import { Logger } from '../logger/logger';
import { PromiseCacher, calculateAge, makeSafariSafeDate } from '@/helpers';
import { OrderService } from '../order/order.service';
import moment from 'moment';
import { AnalyticsService } from '../analytics/analytics.service';
import { ErrorEvent, LogoutCompleteEvent } from '../analytics/events';
import { AppUiService, UiService } from '../ui/ui.service';
import { Routes } from '@/router';
import { AUTOMATIC_PERSONALIZATION_AGE, PaymentProviders, USPaymentProviders } from '@/constants';
import { PersonalizationManagementEvent } from '../analytics/events/personalization-management-event';

export interface CustomerService {
  signIn(request: SignInRequest): Promise<SignInResponse>;
  signOut(): Promise<void>;
  registerUser(newUser: CreateUserRequest): Promise<any>;
  initializeVerification(initializeVerificationRequest: InitializeVerificationRequest): Promise<void>;
  finalizeVerification(finalizeVerificationRequest: FinalizeVerificationRequest): Promise<void>;
  updateCustomer(customerPartial: CustomerProfile | any): Promise<void>;
  updatePassword(oldPassword: string, newPassword: string): Promise<void>;
  resetPasswordRequest(request: ResetPasswordRequest): Promise<CustomerAuthResponse | ApiError>;
  isLoggedIn(): boolean | undefined;
  refreshCustomerProfileInfo(forceRefresh?: boolean, gracefulLogout?: boolean, profileOrSubscriptionOnlyUpdated?: boolean): Promise<void>;
  refreshCustomerDeliveryAddresses(): Promise<void>;
  createCustomerDeliveryAddress(newAddress: CustomerDeliveryAddress): Promise<CustomerDeliveryAddress>;
  updateCustomerDeliveryAddress(updatedAddress: CustomerDeliveryAddress): Promise<void>;
  deleteCustomerDeliveryAddress(addressId: string): Promise<void>;
  isFavoriteRestaurant(restaurantNumber: number): boolean;
  getFavoriteRestaurantName(restaurantNumber: number): string;
  addFavoriteRestaurant(restaurantNumber: number, name: string): void;
  removeFavoriteRestaurant(restaurantNumber: number): void;
  waitForCustomerInitialization(): Promise<boolean>;
  createAutogeneratedUser(): Promise<CreateUserResponse>;
  upgradeGuestUser(request: UpgradeGuestUserRequest): Promise<any>;
  subscribeToMarketingChannel(email: MarketingChannel, loyalty: MarketingContext): Promise<void>;
  unsubscribeToMarketingChannel(channel: MarketingChannel, context: MarketingContext): Promise<void>;
  hasSubscription(channel: MarketingChannel, context: MarketingContext): boolean;
  setCelebrationData(numberOfRewards: number, points: number): void;
  isCustomerAtMaxCreditCards(providerId?: string): boolean;
  isCustomerAtMaxGiftCards(): boolean;
  setJwt(jwt: string): void;
  setOptedIntoEmailMarketingFromOptions(optedIn: boolean): void;
  setOptedIntoPersonalizedMessaging(optedIn: boolean): void;
  setProfile(profile: CustomerProfile | null): void;
  setNewAccountStatus(value: boolean): void;
  setJoinedLoyaltyOnOrderConfirmationPage(flag: boolean): void;
  optInCustomerToSummerOfChipotle(): void;
  updateStudentStatus(accessCode: string, isStudent: boolean): Promise<void>;
  unregisterStudentStatus(): Promise<void>;
  setProfileSubscriptionChangeOnly(profileSubscriptionChangeOnly: boolean): void;
}

export class CustomerError extends Error {
  errorCode: string | undefined;
  errorCount: number | undefined;
  errorMaxTries: number | undefined;
  phoneLastFour: string | undefined;
  constructor(message: string, errorCode?: string, errorCount?: number, errorMaxtries?: number) {
    super(message);
    this.errorCode = errorCode;
    this.errorCount = errorCount;
    this.errorMaxTries = errorMaxtries;
  }
}

export enum CustomerErrorCode {
  ValidationError = 'CAU-0020',
  OldPasswordUsed = 'CAU-0075',
  PasswordResetLinkNotValid = 'CAU-0100',
  RegisterUserEmailInUse = 'CAU-0220',
  UpdateAccountEmailInUse = 'CUS-0006',
  ValidationException = 'CUS-0020',
  BadPhoneCantSubToSMS = 'CUS-0055',
  TwoStepVerificationRequired = 'CAU-0111',
  AccountVerificationRequired = 'CAU-0110',
  AccountLocked = 'CAU-0112',
  InvalidVerificationCode = 'CAU-0113',
  UnableToInitiate2SV = 'CAU-0115',
  ExpiredVerificationCode = 'CAU-0115',
  EmailUpdateRequiresTwoStepVerification = 'CUS-0401',
  PhoneNumberUpdateRequiresTwoStepVerification = 'CUS-0400',
  TwoStepVerificaitonInvalidPassword = 'CAU-0320',
  TwoStepVerificationInvalidPhoneNumber = 'CAU-0118',
  ClearWalletFailed = 'CAU-0530',
  UserAgeIneligible = 'CAU-0045',
  UserUpdateAgeIneligible = 'CUS-0045',
  EmailInvaidFormat = 'CUS-0056',
  PhoneInUse = "CAU-0056",
  UpdatePhoneInUse = "CUS-0056",
  StudentIdAlreadyTaken = "CUS-0500"
}

const EMAIL_IN_USE_MSG =
  'HMMM... It looks like that email is already in use. If you have an existing password, please use it to sign in. Otherwise, try creating your account using a different email address.';
const UNKNOWN_ERR = 'Unknown error';
const CUSTOMER_AUTH_BASE_URL = '/auth/v2/customer';
const CUSTOMER_AUTH_VERIFY_BASE_URL = '/auth/v2/verify';
const CUSTOMER_BASE_URL = '/customer/v2/customer';
const CUSTOMER_SUB_BASE_URL = '/customer/v2';

const MAX_CUSTOMER_CREDIT_CARDS = 2;
const MAX_CUSTOMER_GIFT_CARDS = 1;

export class AppCustomerService extends ApiService implements CustomerService {
  constructor(
    store: Store<AppState>,
    httpClient: AxiosInstance,
    private restaurantService: RestaurantService,
    private orderService: OrderService,
    private promiseCacher: PromiseCacher,
    logger: Logger,
    protected analytics: AnalyticsService,
    private uiService: UiService,
  ) {
    super(store, httpClient, logger, analytics);
  }

  async signIn(request: SignInRequest) {
    const response = await this.httpClient
      .post(`${CUSTOMER_AUTH_BASE_URL}/login`, pascalCaseObject(request))
      .then((r: AxiosResponse<SignInResponse>) => {
        this.trackUserAcrossDevices(r);
        return camelCaseObject(r.data) as SignInResponse
      })
      .catch(e => this.handleCustAuthError('signIn', e));

    if (!response.isSuccess) {
      throw response;
    }

    await this.migrateOrder(response.jwt);

    // as soon as we are done transitioning the order we need to immediately set new JWT for all subsequent requests to work
    this.setBearerToken(response.jwt);
    localStorage.setItem('cmgHasCustomerLoggedInBefore', 'true');

    // Clear the cached last order because the user won't have access to it and shouldn't be able to track it
    if (request.originRoute !== Routes.OrderConfirmation) {
      this.orderService.setLastOrder(null, null);
    }
    return response;
  }

  async signOut() {
    await this.httpClient
      .get(`${CUSTOMER_AUTH_BASE_URL}/logout`)
      .finally(() => {
        this.stopTrackingUserAcrossDevices();
        window.dispatchEvent(new Event('cmg-customer-logout'));
        this.analytics.send(new LogoutCompleteEvent());
        resetStore();
      })
      .catch(e => this.logAndThrowApiError('signOut', e));
  }

  async registerUser(request: CreateUserRequest): Promise<any> {
    try {
      if (this.isInCooldownPeriodAfterFailedRegistration()) {
        return CustomerErrorCode.UserAgeIneligible;
      }

      this.setNewAccountStatus(true);
      const response = (await this.httpClient.post(`${CUSTOMER_AUTH_BASE_URL}/register`, pascalCaseObject(request))) as AxiosResponse<SignInResponse>;
      // We should only be getting this far if 2SV is bypassed (i.e. Canada, etc.)
      const body = camelCaseObject(response) as AxiosResponse<SignInResponse>;
      await this.migrateOrder(body.data.jwt);
      this.setBearerToken(body.data.jwt);
      localStorage.setItem('cmgHasCustomerLoggedInBefore', 'true');
      this.trackUserAcrossDevices(response);
    } catch (error: any) { // Current 2SV design has initial registration always throwing a 400
      const errorCode = error.response?.data?.errorCode;
      if (errorCode === CustomerErrorCode.RegisterUserEmailInUse) {
        throw new CustomerError(EMAIL_IN_USE_MSG);
      } else if (
        errorCode == CustomerErrorCode.TwoStepVerificationRequired ||
        errorCode == CustomerErrorCode.AccountVerificationRequired ||
        errorCode == CustomerErrorCode.UserAgeIneligible
      ) {
        if (errorCode == CustomerErrorCode.UserAgeIneligible) {
          localStorage.setItem('IneligibleUserAccountCreationAttemptDateTime', new Date().toISOString());
        }
        return errorCode;
      } else if (errorCode == CustomerErrorCode.ValidationException) {
        const innerError = error.response?.data?.validationErrors?.map((error: ValidationError) => ({
          errorCode: error.errorCode,
          errorData: null,
          errorDescription: error.message,
        }));
        if (innerError) {
          this.logger.error('Validation Error', undefined, innerError);
          throw new CustomerError(innerError.errorDescription)
        } else {
          throw new CustomerError(UNKNOWN_ERR);
        }
      } else if (errorCode == CustomerErrorCode.PhoneInUse) {
        this.logger.error(error.response?.data?.message, undefined, undefined);
        throw new CustomerError(error.response?.data?.errorCode);
      } else {
        this.logger.error('Unknown error registering customer', undefined, error);
        throw new CustomerError(UNKNOWN_ERR);
      }
    }
  }

  async initializeVerification(initializeVerificationRequest: InitializeVerificationRequest): Promise<void> {
    try {
      await this.httpClient.post(`${CUSTOMER_AUTH_VERIFY_BASE_URL}/initialize`, pascalCaseObject(initializeVerificationRequest));
    } catch (error: any) {
      const errorData = error.response?.data;
      switch (errorData?.errorCode) {
        case CustomerErrorCode.TwoStepVerificaitonInvalidPassword:
        case CustomerErrorCode.TwoStepVerificationInvalidPhoneNumber:
        case CustomerErrorCode.AccountLocked:
        case CustomerErrorCode.PhoneInUse:
          throw new CustomerError(errorData.message, errorData.errorCode);
        default:
          this.logger.error('Unknown error initializing two step verification', undefined, error);
          throw new CustomerError(UNKNOWN_ERR);
      }
    }
  }

  async finalizeVerification(finalizeVerificationRequest: FinalizeVerificationRequest): Promise<void> {
    try {
      const response = (await this.httpClient.post(`${CUSTOMER_AUTH_VERIFY_BASE_URL}/finalize`, pascalCaseObject(finalizeVerificationRequest))) as AxiosResponse<FinalizeVerificationResponse>;
      const body = camelCaseObject(response) as AxiosResponse<FinalizeVerificationResponse>;

      if (body.data.jwt) { // Not all verification actions will include a jwt (i.e. update phone number)
        await this.migrateOrder(body.data.jwt);
        this.setBearerToken(body.data.jwt);
        localStorage.setItem('cmgHasCustomerLoggedInBefore', 'true');
      }

    } catch (error: any) {
      if (error.response && error.response.data &&
        [CustomerErrorCode.InvalidVerificationCode, CustomerErrorCode.ExpiredVerificationCode, CustomerErrorCode.AccountLocked].indexOf(error.response.data.errorCode) > -1) {
        throw new CustomerError(error.response.data.message, error.response.data.errorCode, error.response.data.errorCount, error.response.data.errorMaxTries);
      }
      this.logger.error('Unknown error finalizing two step verification', undefined, error);
      throw error;
    }
  }

  async migrateOrder(newBearerToken: string) {
    if (this.store.state.customer.jwt) {
      const order = this.orderService.getCurrentOrder();
      if (order) {
        try {
          await this.orderService.changeCustomerOnOrder(order, this.getJwtFromBearerToken(newBearerToken));
        } catch {
          this.orderService.resetPendingOrders();
          this.logger.warn(`session likely expired so customer lost their order: ${order.order.orderId}`);
        }
      }
    }
  }

  isLoggedIn(): boolean | undefined {
    return this.store.getters[CUSTOMER_GETTERS.IS_LOGGED_IN];
  }

  async refreshCustomerProfileInfo(forceRefresh = true, gracefulLogout = false, profileOrSubscriptionOnlyUpdated = false) {
    if (!this.store.state.customer.jwt) {
      return;
    }

    return this.promiseCacher.exec('refreshCustomerProfileInfo', forceRefresh, async () => {
      const response = await this.httpClient
        .get(`${CUSTOMER_BASE_URL}`)
        .then((r: AxiosResponse<GetCustomerResponse>) => camelCaseObject<GetCustomerResponse>(r.data))
        .catch(e => {
          if (!gracefulLogout) {
            this.logAndThrowApiError('refreshCustomerProfileInfo', e);
          } else {
            return Promise.reject();
          }
        });

      const profile: CustomerProfile = {
        ...response,
        dateOfBirth: !!response.dateOfBirth
          ? moment(response.dateOfBirth)
            .set({ hour: 0 })
            .toDate()
          : undefined,
        subscriptions: response.subscriptions
          ? response.subscriptions.map(s => ({
            ...s,
            created: makeSafariSafeDate(s.created),
            statusUpdated: makeSafariSafeDate(s.created),
          }))
          : [],
        dietaryTags: response.dietaryTags ? response.dietaryTags : [],
      };

      const customerType = profile.isGuest ? CustomerType.Guest : CustomerType.Normal;

      this.store.commit(CUSTOMER_MUTATIONS.REFRESH_PROFILE, profile);
      this.setCustomerType(customerType);

      // Update loyalty enrollment status
      const isEnrolled = !profile.isGuest && !!profile.subscriptions && profile.subscriptions.some(x => x.context === 'loyalty' && (x.status === CustomerSubscriptionStatus.Subscribed || x.status === CustomerSubscriptionStatus.SubscribePending));
      this.store.commit(CUSTOMER_MUTATIONS.SET_LOYALTY_IS_ENROLLED, isEnrolled);
      this.store.commit(CUSTOMER_MUTATIONS.SET_OPTED_INTO_PERSONALIZED_MESSAGING, profile.optedIntoPersonalization ?? false);
      this.store.commit(CUSTOMER_MUTATIONS.SET_OPTED_INTO_SUMMER_OF_EXTRAS_YEAR, profile.optedIntoSummerOfExtrasYear);
      // Alert AEM and Target the customer profile refreshed
      window.dispatchEvent(new Event('cmg-customer-refresh-profile'));

      const promises: Promise<any>[] = [];

      if (!profileOrSubscriptionOnlyUpdated) {
        if (response.favoriteRestaurants) {
          const favoritesById: { [key: number]: string } = {};
          Object.keys(response.favoriteRestaurants).forEach((favorite, i) => {
            if (response.favoriteRestaurants) {
              const restaurant = response.favoriteRestaurants[i];
              favoritesById[restaurant.restaurantNumber] = restaurant.name;
            }
          });

          this.store.commit(CUSTOMER_MUTATIONS.SET_FAVORITE_RESTAURANTS, favoritesById);
          promises.push(this.restaurantService.refreshFavoriteRestaurantData());
        }

        promises.push(this.restaurantService.refreshMostRecentRestaurant());
      }

      return Promise.all(promises) as Promise<any>;
    });
  }

  isFavoriteRestaurant(restaurantNumber: number) {
    return this.store.state.customer.favoriteRestaurants[restaurantNumber] != null;
  }

  getFavoriteRestaurantName(restaurantNumber: number) {
    return this.store.state.customer.favoriteRestaurants[restaurantNumber];
  }

  addFavoriteRestaurant(restaurantNumber: number, name: string) {
    if (restaurantNumber == null) {
      return;
    }

    const favoritesById = Object.assign({}, this.store.state.customer.favoriteRestaurants);
    favoritesById[restaurantNumber] = name;

    this.store.commit(CUSTOMER_MUTATIONS.SET_FAVORITE_RESTAURANTS, favoritesById);

    this.updateCustomer({
      favoriteRestaurants: this.favoritesIdToArray(favoritesById),
    });

    this.restaurantService.refreshFavoriteRestaurantData();
  }

  removeFavoriteRestaurant(restaurantNumber: number) {
    if (this.store.state.customer.favoriteRestaurants[restaurantNumber] != null) {
      const favoritesById = Object.assign({}, this.store.state.customer.favoriteRestaurants);
      delete favoritesById[restaurantNumber];
      this.store.commit(CUSTOMER_MUTATIONS.SET_FAVORITE_RESTAURANTS, favoritesById);

      this.updateCustomer({
        favoriteRestaurants: this.favoritesIdToArray(favoritesById),
      });

      this.restaurantService.removeFavoriteById(restaurantNumber);
    }
  }

  favoritesIdToArray(favoritesById: { [key: number]: string }) {
    const favoritesArray = [];
    for (const favoriteId in favoritesById) {
      if (favoritesById.hasOwnProperty(favoriteId)) {
        favoritesArray.push({
          restaurantNumber: favoriteId,
          name: favoritesById[favoriteId],
        });
      }
    }
    return favoritesArray;
  }

  async updateCustomer(customerPartial: CustomerProfile | {}): Promise<any> {
    // Due to some Gigya weirdness we need to manually set that param to be camal case
    const partial: any = pascalCaseObject(customerPartial);

    const keepCamel = [
      'isGuacMode',
      'optedIntoPersonalization',
      'optedIntoExtras',
      'optedIntoSummerOfExtrasYear',
      'optedInSummer2025',
      'cyobLastSeenYear',
      'hasSeenRewardsExchOnboarding',
      'hasSeenRewardsGenOnboarding',
      'signupCampaign',
      'signupCampaignChannelId'
    ] as const;

    for (const key of keepCamel) {
      const pascal = `${key[0].toUpperCase()}${key.slice(1)}`;
      if (partial[pascal] !== undefined) {
        partial[key] = partial[pascal];
        delete partial[pascal];
      }
    }

    return await this.httpClient.put(`${CUSTOMER_BASE_URL}`, partial).catch((error: AxiosError) => {
      const apiError = error as AxiosError<ApiError, any>;
      switch (apiError.response?.data?.errorCode) {
        case CustomerErrorCode.UpdateAccountEmailInUse:
        case CustomerErrorCode.EmailUpdateRequiresTwoStepVerification:
        case CustomerErrorCode.PhoneNumberUpdateRequiresTwoStepVerification:
        case CustomerErrorCode.UserUpdateAgeIneligible:
        case CustomerErrorCode.UpdatePhoneInUse:
          throw new CustomerError(apiError.response.data.message, apiError.response.data.errorCode);
      }

      this.logAndThrowApiError('updateCustomer', error);
    });
  }

  async updateStudentStatus(accessCode: string, isStudent: boolean): Promise<void> {
    try {
      await this.httpClient.post(`${CUSTOMER_SUB_BASE_URL}/student/register/${accessCode}`);
      await this.refreshCustomerProfileInfo();
    } catch (error: any) {
      const apiError = error as AxiosError<ApiError, any>;

      if (apiError.response?.data?.errorCode === CustomerErrorCode.StudentIdAlreadyTaken) {
        throw new CustomerError(
          'Student ID is already linked to an existing account',
          apiError.response.data.errorCode
        );
      }

      this.logAndThrowApiError('updateStudentStatus', error);
    }
  }

  async unregisterStudentStatus(): Promise<void> {
    try {
      await this.httpClient.delete(`${CUSTOMER_SUB_BASE_URL}/student/unregister`);
      await this.refreshCustomerProfileInfo();
    } catch (error: any) {
      this.logAndThrowApiError('unregisterStudentStatus', error);
    }
  }

  async updatePassword(oldPassword: string, newPassword: string): Promise<any> {
    return await this.httpClient.put(`${CUSTOMER_AUTH_BASE_URL}/password/change`, { Password: oldPassword, NewPassword: newPassword }).catch(error => {
      if (error.response && error.response.data) {
        if (error.response.data.errorCode == CustomerErrorCode.ValidationError && error.response.data.validationErrors && error.response.data.validationErrors.length > 0) {
          throw new CustomerError(error.response.data.validationErrors[0].message);
        } else if (error.response.data.errorCode == CustomerErrorCode.OldPasswordUsed) {
          throw new CustomerError('An old password cannot be reused.');
        } else if (error.response.data.errorCode == CustomerErrorCode.ClearWalletFailed) {
          throw new CustomerError(error.response.data.message, error.response.data.errorCode);
        } else {
          this.logAndThrowApiError('updatePassword', error);
        }
      } else {
        this.logAndThrowApiError('updatePassword', error);
      }
    });
  }

  async resetPasswordRequest(request: ResetPasswordRequest): Promise<any> {
    return await this.httpClient.put(`${CUSTOMER_AUTH_BASE_URL}/password/forgot`, request).catch(error => {
      if (!!error.response && !!error.response.data) {
        const errorResponse = error.response.data;

        switch (errorResponse.errorCode) {
          case CustomerErrorCode.OldPasswordUsed:
            throw new CustomerError('New password must be different than your last 7 passwords.');
          case CustomerErrorCode.PasswordResetLinkNotValid:
            throw new CustomerError('Password reset link is no longer valid.');
          default:
            this.logAndThrowApiError('resetPasswordRequest', error);
        }
      }
    });
  }

  async optInCustomerToSummerOfChipotle(): Promise<void> {
    const currentYear = new Date().getFullYear();

    this.store.commit(CUSTOMER_MUTATIONS.SET_OPTED_INTO_SUMMER_OF_EXTRAS_YEAR, currentYear);
    this.updateCustomer({ optedIntoSummerOfExtrasYear: currentYear });
  }

  async refreshCustomerDeliveryAddresses() {
    const deliveryAddress = await this.httpClient
      .get(`${CUSTOMER_SUB_BASE_URL}/delivery/address`)
      .then((r: AxiosResponse<CustomerDeliveryAddress>) => r.data)
      .catch(e => this.logAndThrowApiError('refreshCustomerDeliveryAddresses', e));

    this.store.commit(CUSTOMER_MUTATIONS.SET_DELIVERY_ADDRESSES, deliveryAddress);
  }

  async createCustomerDeliveryAddress(newAddress: CustomerDeliveryAddress) {
    const newDeliveryAddress = await this.httpClient
      .post(`${CUSTOMER_SUB_BASE_URL}/delivery/address`, newAddress)
      .then((r: AxiosResponse<CustomerDeliveryAddress>) => r.data)
      .catch(e => this.logAndThrowApiError('createCustomerDeliveryAddress', e));

    this.refreshCustomerDeliveryAddresses();
    return newDeliveryAddress;
  }

  async updateCustomerDeliveryAddress(updatedAddress: CustomerDeliveryAddress) {
    await this.httpClient
      .put(`${CUSTOMER_SUB_BASE_URL}/delivery/address`, updatedAddress)
      .then((r: AxiosResponse<CustomerDeliveryAddress>) => r.data)
      .catch(e => this.logAndThrowApiError('updateCustomerDeliveryAddress', e));

    this.refreshCustomerDeliveryAddresses();
  }

  async deleteCustomerDeliveryAddress(addressId: string) {
    await this.httpClient
      .delete(`${CUSTOMER_SUB_BASE_URL}/delivery/address/${addressId}`)
      .then((r: AxiosResponse<CustomerDeliveryAddress>) => r.data)
      .catch(e => this.logAndThrowApiError('deleteCustomerDeliveryAddress', e));

    this.refreshCustomerDeliveryAddresses();
  }

  async createAutogeneratedUser(): Promise<CreateUserResponse> {
    return this.createUser(
      {
        isAnonymous: false,
        isGuest: true,
        emailAddress: `${uuid()}@chipotle.com`,
        firstName: 'Guest',
        lastName: 'User',
        phoneNumber: '999-999-9999',
      },
      {
        customerType: CustomerType.AutoGenerated,
        setAsCustomer: true,
      },
    );
  }

  /**
   * This is so one can know during page load if still waiting for customer profile fetch
   * Resolves to TRUE if customer is logged in and not a guest
   */
  waitForCustomerInitialization(): Promise<boolean> {
    const customerLoginState = this.isLoggedIn();
    if (customerLoginState != undefined) {
      return Promise.resolve(customerLoginState);
    }

    return new Promise((res, rej) => {
      const profileWatcher = this.store.watch(
        state => state.customer.profile,
        () => {
          res(!!this.store.state.customer.profile && !this.store.state.customer.profile.isGuest);

          profileWatcher();
        },
      );
    });
  }

  async upgradeGuestUser(request: UpgradeGuestUserRequest): Promise<any> {
    try {
      await this.httpClient.post(`${CUSTOMER_AUTH_BASE_URL}/upgrade`, request);
    } catch (error: any) {
      if (error.response && error.response.data) {
        const errorCode = error.response.data.errorCode;

        switch (errorCode) {
          case CustomerErrorCode.RegisterUserEmailInUse:
            throw new CustomerError(EMAIL_IN_USE_MSG);
            break;
          case CustomerErrorCode.TwoStepVerificationRequired:
          case CustomerErrorCode.AccountVerificationRequired:
          case CustomerErrorCode.UserAgeIneligible:
            return errorCode;
            break;
        }

      }

      throw error;
    }
  }

  subscribeToMarketingChannel(channel: MarketingChannel, context: MarketingContext): Promise<any> {
    const campaignId = this.store.state.ui.campaignId ?? '';
    const campaignChannelId = this.store.state.ui.campaignChannelId ?? '';
    const referrerCustomerIdHash = this.store.state.ui.referrerCustomerIdHash ?? '';

    const referralParam = (referrerCustomerIdHash && (context.includes(MarketingContext.Loyalty))) ? `&referredToLoyaltyBy=${referrerCustomerIdHash}` : '';

    return this.httpClient.put(`${CUSTOMER_SUB_BASE_URL}/subscription/${channel}?context=${context}&campaignId=${campaignId}&campaignChannelId=${campaignChannelId}${referralParam}`)
      .catch(err => {
        if (err.response && err.response.data) {
          const data = err.response.data;

          if (data.errorCode == CustomerErrorCode.ValidationException && data.validationErrors && data.validationErrors.length > 0) {
            this.analytics.send(new ErrorEvent('account', data.validationErrors[0].message, 'edit-account'));
            throw new CustomerError(data.validationErrors[0].message);
          }
        }

        this.logAndThrowApiError('subscribeToMarketingChannel', err);
      });
  }

  unsubscribeToMarketingChannel(channel: MarketingChannel, context: MarketingContext): Promise<any> {
    return this.httpClient.delete(`${CUSTOMER_SUB_BASE_URL}/subscription/${channel}?context=${context}`);
  }

  hasSubscription(channel: MarketingChannel, context: MarketingContext): boolean {
    const customer = this.store.state.customer.profile;
    if (!customer) {
      return false;
    }

    return some(
      customer.subscriptions,
      s =>
        s.channel.toLowerCase() == channel &&
        s.context.toLowerCase() == context &&
        s.status !== CustomerSubscriptionStatus.Unsubscribed &&
        s.status !== CustomerSubscriptionStatus.UnsubscribePending,
    );
  }

  handleCustAuthError(operationName: string, error: AxiosError): never {
    if (this.isAxiosErrorWithStatusCode(error, 400)) {
      this.logger.warn(`Something went wrong while executing ${operationName}, 400 error`);
    } else {
      this.logger.error(`Something went wrong while executing ${operationName}`);
    }

    if (error.response) {
      this.trackUserAcrossDevices(error.response);
    }

    if (!error.response) {
      throw error;
    }

    const apiError = error as AxiosError<ApiError, any>;
    if (apiError.response) {
      const custAuthError: AuthError = { status: error.response.status, data: apiError.response.data };
      throw custAuthError;
    }

    throw error;
  }

  setCelebrationData(numberOfRewards: number, points: number): void {
    this.store.commit(CUSTOMER_MUTATIONS.SET_CELEBRATION_NUMBER_OF_REWARDS, numberOfRewards);
    this.store.commit(CUSTOMER_MUTATIONS.SET_CELEBRATION_POINTS, points);
    // this.store.commit(CUSTOMER_MUTATIONS.SET_REWARD_POINTS, points);
    this.store.commit(CUSTOMER_MUTATIONS.SET_CELEBRATION_LAST_UPDATED, new Date());
  }

  isCustomerAtMaxCreditCards(providerId?: string): boolean {
    let customerCards = this.store.state.customer.wallet.paymentCards.filter(card => card.isGiftCard == false);
    const isUSProvider = USPaymentProviders.includes(providerId || '');

    if (providerId) {
      customerCards = customerCards.filter(card =>
        !isUSProvider
          ? card.paymentProviderId == providerId
          : USPaymentProviders.includes(card.paymentProviderId || ''))
    }
    return customerCards.length >= MAX_CUSTOMER_CREDIT_CARDS;
  }

  isCustomerAtMaxGiftCards(): boolean {
    return this.store.state.customer.wallet.paymentCards.filter(card => card.isGiftCard == true).length >= MAX_CUSTOMER_GIFT_CARDS;
  }

  setJwt(jwt: string) {
    this.store.commit(CUSTOMER_MUTATIONS.SET_JWT, jwt);
  }

  setOptedIntoEmailMarketingFromOptions(optedIn: boolean) {
    this.store.commit(CUSTOMER_MUTATIONS.SET_OPTED_INTO_EMAIL_MARKETING_FROM_OPTIONS, optedIn);
  }

  setOptedIntoPersonalizedMessaging(optedIn: boolean) {
    this.store.commit(CUSTOMER_MUTATIONS.SET_OPTED_INTO_PERSONALIZED_MESSAGING, optedIn);
  }

  setProfile(profile: CustomerProfile | null) {
    this.store.commit(CUSTOMER_MUTATIONS.SET_CUSTOMER_PROFILE, profile);
  }

  setNewAccountStatus(value: boolean) {
    this.store.commit(CUSTOMER_MUTATIONS.SET_NEW_ACCOUNT, value);
  }

  setJoinedLoyaltyOnOrderConfirmationPage(flag: boolean): void {
    this.store.commit(CUSTOMER_MUTATIONS.SET_JOINED_LOYALTY_ON_ORDER_CONFIRMATION_PAGE, flag);
  }

  setProfileSubscriptionChangeOnly(profileSubscriptionChangeOnly: boolean) {
    this.store.commit(CUSTOMER_MUTATIONS.SET_PROFILE_SUBSCRIPTION_CHANGE_ONLY, profileSubscriptionChangeOnly);
  }

  private async createUser(request: CreateUserRequest, { setAsCustomer, customerType }: { setAsCustomer: boolean; customerType: CustomerType }): Promise<CreateUserResponse> {
    const response = await this.httpClient
      .post(`${CUSTOMER_AUTH_BASE_URL}/register`, request)
      .then((res: AxiosResponse<CreateUserResponse>) => {
        const body = camelCaseObject<CreateUserResponse>(res.data);
        if (!body || !body.isSuccess) {
          throw body;
        }

        return body;
      })
      .catch(e => this.logAndThrowApiError('createUser', e));

    if (setAsCustomer && response && response.jwt && response.isSuccess) {
      this.setBearerToken(response.jwt);
      this.setCustomerType(customerType);

      this.refreshCustomerProfileInfo();
    }

    return response;
  }

  /**
   * @param token Token in the form of "Bearer <jwt>"
   */
  private setBearerToken(token: string) {
    this.setJwt(this.getJwtFromBearerToken(token));
  }

  private getJwtFromBearerToken(token: string): string {
    return token.split(' ')[1];
  }

  private setCustomerType(type: CustomerType) {
    this.store.commit(CUSTOMER_MUTATIONS.SET_CUSTOMER_TYPE, type);
  }

  // Ketch cookie banner implementation
  private trackUserAcrossDevices(response: AxiosResponse): void {
    let headers = response?.headers;
    if (headers && headers instanceof AxiosHeaders && headers.has('X-Account-Hash')) {
      let accountHash: AxiosHeaderValue = headers.get('X-Account-Hash');
      if (accountHash instanceof AxiosHeaders || accountHash instanceof Object) {
        localStorage.setItem('cmg-pvc', JSON.stringify(accountHash));
      } else if (typeof accountHash === 'string') {
        localStorage.setItem('cmg-pvc', accountHash);
      }
    }
  }

  private stopTrackingUserAcrossDevices(): void {
    localStorage.removeItem('cmg-pvc');
  }

  private isInCooldownPeriodAfterFailedRegistration(): boolean {
    const ineligibleAttemptTime = localStorage.getItem('IneligibleUserAccountCreationAttemptDateTime');
    if (!ineligibleAttemptTime) return false;

    const lastAttempt = new Date(ineligibleAttemptTime);
    const now = new Date();
    const timeDifference = now.getTime() - lastAttempt.getTime();
    const cooldownPeriod = 4 * 60 * 60 * 1000;

    return timeDifference < cooldownPeriod;
  }
}
